import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Copy, Plus, Trash2, Pencil, Users, RefreshCw } from "lucide-react";
import Modal from "./Modal";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Clients = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Para feedback de copy por fila
  const [copiedId, setCopiedId] = useState(null);

  const [idDelete, setIdDelete] = useState("");
  const userId = sessionStorage.getItem("_id");

  const stats = useMemo(() => {
    const total = data.length;
    const activos = data.filter((x) => x.estado === "Activo").length;
    const inactivos = data.filter((x) => x.estado === "Inactivo").length;
    return { total, activos, inactivos };
  }, [data]);

  const handleCopy = async (id) => {
    try {
      await navigator.clipboard.writeText(id);

      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 900);

      toast.info("✅ ID copiado", {
        position: "top-center",
        autoClose: 900,
        hideProgressBar: true,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: false,
        theme: "colored",
        transition: Bounce,
      });
    } catch (error) {
      toast.error("No se pudo copiar.", {
        position: "top-right",
        theme: "colored",
        transition: Bounce,
      });
      console.error("Error al copiar:", error);
    }
  };

  const fetchData = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_SERVER_PATH + "api/clientes/" + userId
      );
      setData(response?.data?.cuits ?? []);
    } catch (error) {
      console.error("Error al obtener los datos", error);
      toast.error("Error al cargar clientes.", {
        position: "top-right",
        theme: "colored",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const deleteClient = async () => {
    try {
      await axios.delete(
        process.env.REACT_APP_SERVER_PATH +
          "api/clientes/" +
          sessionStorage.getItem("_id") +
          "/cuits/" +
          idDelete
      );

      setIsOpen(false);
      toast.success("Cliente eliminado.", {
        position: "top-right",
        autoClose: 1500,
        theme: "colored",
        transition: Bounce,
      });
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar.", {
        position: "top-right",
        autoClose: false,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  const handleDelete = (id) => {
    setIdDelete(id);
    setIsOpen(true);
  };

  const badgeEstado = (estado) => {
    if (estado === "Activo")
      return (
        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-600/20 ring-inset">
          Activo
        </span>
      );
    if (estado === "Inactivo")
      return (
        <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-600/20 ring-inset">
          Inactivo
        </span>
      );
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 ring-1 ring-amber-600/20 ring-inset">
        {estado || "N/A"}
      </span>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Eliminar cliente"
        message="¿Seguro que querés eliminar este cliente? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={deleteClient}
      />

      <div className="min-h-screen bg-gray-50 px-4 py-8">
        <div className="mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-white p-3 shadow-sm ring-1 ring-gray-200">
                <Users className="h-6 w-6 text-blue-700" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                  Clientes
                </h1>
                <p className="text-sm text-gray-600">
                  Gestioná tus CUITs, alias y estado de integración.
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={fetchData}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
              >
                <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
                Actualizar
              </button>

              <button
                type="button"
                onClick={() => navigate("/new-client")}
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
              >
                <Plus className="h-4 w-4" />
                Nuevo cliente
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <p className="text-xs font-semibold text-gray-500">Total</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <p className="text-xs font-semibold text-gray-500">Activos</p>
              <p className="mt-1 text-2xl font-bold text-emerald-700">
                {stats.activos}
              </p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
              <p className="text-xs font-semibold text-gray-500">Inactivos</p>
              <p className="mt-1 text-2xl font-bold text-rose-700">
                {stats.inactivos}
              </p>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50 text-xs font-semibold text-gray-600">
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-4">ID</th>
                    <th className="px-6 py-4">Copiar</th>
                    <th className="px-6 py-4">CUIT</th>
                    <th className="px-6 py-4">Alias</th>
                    <th className="px-6 py-4">Vencimiento</th>
                    <th className="px-6 py-4">Estado</th>
                    <th className="px-6 py-4 text-right">Acciones</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center text-gray-500">
                        Cargando clientes...
                      </td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-10 text-center">
                        <p className="text-sm font-semibold text-gray-900">
                          No tenés clientes cargados
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                          Creá tu primer cliente para comenzar.
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate("/new-client")}
                          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                        >
                          <Plus className="h-4 w-4" />
                          Nuevo cliente
                        </button>
                      </td>
                    </tr>
                  ) : (
                    data.map((item, index) => (
                      <tr key={item._id || index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono text-xs text-gray-700">
                          {item._id}
                        </td>

                        <td className="px-6 py-4">
                          <button
                            type="button"
                            onClick={() => handleCopy(item._id)}
                            className={[
                              "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm transition",
                              copiedId === item._id
                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50",
                            ].join(" ")}
                          >
                            <Copy className="h-4 w-4" />
                            {copiedId === item._id ? "Copiado" : "Copiar"}
                          </button>
                        </td>

                        <td className="px-6 py-4 text-gray-900">{item.cuit}</td>
                        <td className="px-6 py-4 text-gray-700">{item.alias}</td>

                        <td className="px-6 py-4 text-gray-700">
                          {item.fechaVencimiento
                            ? new Date(item.fechaVencimiento).toLocaleDateString("es-AR")
                            : "-"}
                        </td>

                        <td className="px-6 py-4">{badgeEstado(item.estado)}</td>

                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 shadow-sm hover:bg-gray-50"
                              onClick={() =>
                                toast.info("Editar: en progreso", {
                                  position: "top-right",
                                  autoClose: 1200,
                                  theme: "colored",
                                  transition: Bounce,
                                })
                              }
                            >
                              <Pencil className="h-4 w-4" />
                              Editar
                            </button>

                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-rose-700"
                              onClick={() => handleDelete(item.cuit)}
                            >
                              <Trash2 className="h-4 w-4" />
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
              <p className="text-xs text-gray-500">
                Mostrando <span className="font-semibold text-gray-800">{data.length}</span>{" "}
                cliente(s)
              </p>
              <p className="text-xs text-gray-500">FEAPI • Panel</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Clients;
